import React, { useContext, useEffect, useRef, useState } from "react";
import "./MobileProductList.css";
import { Box, CircularProgress, Drawer } from "@mui/material";
import PosCustomIcon from "../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { MagnifyingGlassIcon, PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { UserContext } from "../../../../App";
import { getGlobalProductsList } from "../../../../config/Services";
import vegIcon from "../../../../assets/images/veg-new.png";
import nonVegIcon from "../../../../assets/images/non-veg-new.png";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import { debounce } from "lodash";
import CartItemDrawer from "./components/cartItemTable/Drawer/CartItemDrawer/CartItemDrawer";
import { getStoreCurrencySymbol } from "../../../../utils/currency";

const MobileProductList = ({
  category,
  openProductList,
  setOpenProductList,
  increseItemQuantityTroughParticularCategory,
  setAddOnModalOpen,
  setAddOnProductList,
  setVariantProductList,
  setSelectedProductForCart,
  setGoToMobileCategory,
  addItemLoader,
  setAddItemLoader,
}) => {
  const [productList, setProductList] = useState([]);
  const [productsLoader, setProductsLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState("");
  const pageSize = 20;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProduct, setDrawerProduct] = useState(null);

  const handleProductTileClick = (item, productImage, sellingPrice) => {
    const gtin = item?.variants?.[0]?.barcodes?.[0] || null;
    setDrawerProduct({
      gtin,
      productName: item?.name,
      productImage,
      sellingPrice,
      variantName: item?.variants?.length > 1 ? item?.variants?.[0]?.variantName : undefined,
    });
    setDrawerOpen(true);
  };

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const { saleMode } = useContext(UserContext);

  const observerRef = useRef(null);
  const debouncedSearchRef = useRef(null);

  const handleMobProductListClose = () => {
    setOpenProductList(false);
  };

  const fetchProductList = (pageNo, query = "") => {
    if (productsLoader) return;

    if (pageNo !== 1 && !hasMore) return;

    setProductsLoader(true);

    const payload = {
      page: pageNo,
      pageSize: pageSize,
      query: query || null,
      storeLocations: [locId],
      storeIds: [orgId],
      productTypes: ["MENU"],
      salesChannels: [saleMode],
    };
    if (!query) {
      payload.appCategories = { categoryLevel1: [category] };
    }
    getGlobalProductsList(payload)
      .then((res) => {
        const response = res?.data?.data?.data;
        const newData = response?.data || [];

        // setProductList((prev) => [...prev, ...newData]);
        setProductList((prev) => (pageNo === 1 ? newData : [...prev, ...newData]));

        const totalRecords = response?.totalRecords || 0;
        const loadedRecords = pageNo * pageSize;

        if (loadedRecords >= totalRecords || newData.length === 0) {
          setHasMore(false);
        }
        setPage(pageNo);
        setProductsLoader(false);
      })
      .catch((err) => {
        setProductsLoader(false);
      });
  };

  useEffect(() => {
    if (openProductList && category) {
      setProductList([]);
      setPage(1);
      setHasMore(true);
      fetchProductList(1);
    }
  }, [openProductList, category]);

  const lastItemRef = (node) => {
    if (productsLoader) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          // fetchProductList(page + 1);
          fetchProductList(page + 1, searchText);
        }
      },
      {
        root: document.querySelector(".mobileProductData"),
        threshold: 0.2,
      }
    );

    if (node) observerRef.current.observe(node);
  };

  const isItemAvailable = (startTime, endTime) => {
    if (!startTime || !endTime) {
      return true;
    }

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes >= startMinutes) {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    }

    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearchRef.current(value);
  };

  const handleClearSearch = () => {
    debouncedSearchRef.current?.cancel();
    setSearchText("");
    setProductList([]);
    setPage(1);
    setHasMore(true);
    fetchProductList(1, "");
  };

  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => {
      setProductList([]);
      setPage(1);
      setHasMore(true);
      fetchProductList(1, value);
    }, 500);

    return () => {
      debouncedSearchRef.current?.cancel();
    };
  }, [category, saleMode]);

  return (
    <>
      <Drawer
        anchor="bottom"
        open={openProductList}
        onClose={() => {}}
        ModalProps={{ disableEscapeKeyDown: true, sx: { zIndex: 1500 } }}
      >
        <Box
          sx={{
            width: "100vw",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
            p: 2,
            overflow: "hidden",
          }}
          role="presentation"
        >
          <div className="mobileCategoryHeaderContainer">
            <div className="mobileCategoryHeader">
              <p>{category}</p>
              <PosCustomIcon
                icon={<XMarkIcon />}
                height={"20px"}
                width={"20px"}
                pointer={true}
                onClickFunction={handleMobProductListClose}
              />
            </div>
            <div className="mobileOptionsInputWrapper">
              <PosCustomIcon
                icon={<MagnifyingGlassIcon />}
                color={"#6B6B6B"}
                height={"1rem"}
                width={"1rem"}
              />
              <input
                className={`mobileOptionsSearch`}
                type="text"
                placeholder="Search Products"
                value={searchText}
                onChange={handleSearchChange}
              />
              {searchText && (
                <div className="mobileSearchClearIcon">
                  <p onClick={handleClearSearch}>clear</p>
                </div>
              )}
            </div>
          </div>
          <div className="mobileProductData">
            {productList &&
              productList?.length > 0 &&
              productList?.map((item, idx) => {
                const isLastItem = idx === productList.length - 1;
                const productImage =
                  item?.variants?.[0]?.images?.front ||
                  item?.variants?.[0]?.images?.back ||
                  item?.variants?.[0]?.images?.left ||
                  item?.variants?.[0]?.images?.right ||
                  item?.variants?.[0]?.images?.top ||
                  item?.variants?.[0]?.images?.bottom ||
                  item?.variants?.[0]?.images?.top_left ||
                  item?.variants?.[0]?.images?.top_right ||
                  "https://tse4.mm.bing.net/th?id=OIP.mPwk4FItjNeb2Oaq8F1u9QHaGE&pid=Api&P=0";

                const selectedSaleChannel = item?.variants?.[0]?.externalSalesChannels?.find(
                  (channel) => channel?.salesChannelName === saleMode
                );

                const sellingPrice =
                  selectedSaleChannel?.salePrice ||
                  item?.variants?.[0]?.inventorySync?.sellingPrice;

                const shortCode = item?.variants[0]?.shortCode || "";

                const startTime = item?.itemAvailableStartTime;
                const endTime = item?.itemAvailableEndTime;

                const isAvailable = isItemAvailable(startTime, endTime);

                return (
                  <div
                    key={idx}
                    className="mobProuductsCard"
                    ref={isLastItem ? lastItemRef : null}
                    onClick={() => handleProductTileClick(item, productImage, sellingPrice)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="mobProductsLeftContainer">
                      <div className="mobProductImg">
                        <img src={productImage} alt="image" height={"100%"} width={"100%"} />
                      </div>
                      <div className="mobProductName">
                        <p>{item?.name}</p>
                        <div className="mobPriceContainer">
                          <PosCustomIcon
                            image={item?.foodType === "VEG" ? vegIcon : nonVegIcon}
                            height={"16px"}
                            width={"16px"}
                          />
                          <p className="mobProductPrice">{getStoreCurrencySymbol()} {sellingPrice}</p>
                          {shortCode && <p className="mobShortCode">{`[${shortCode}]`}</p>}
                        </div>
                      </div>
                    </div>
                    <div className="mobProductsRightContainer">
                      <PosCustomButton
                        variant="blue-s"
                        title={
                          <PosCustomIcon
                            icon={<PlusIcon />}
                            height={"13px"}
                            width={"13px"}
                            color={!isAvailable ? "#999" : "#0860E6"}
                            loaderSize={"13px"}
                          />
                        }
                        height={"32px"}
                        width={"24px"}
                        fontWeight={400}
                        radius={"4px"}
                        onClickFunction={(e) => {
                          e?.stopPropagation();
                          if (!isAvailable || addItemLoader !== null) return;

                          const isWeighing = item?.needsWeighingScaleIntegration;

                          const hasAddons =
                            item?.variants?.length > 1 ||
                            (item?.variants?.[0]?.addOn && item?.variants?.[0]?.addOn?.length > 0);

                          if (hasAddons && !isWeighing) {
                            setOpenProductList(false);
                            setGoToMobileCategory(false);

                            setAddOnProductList(item?.variants?.[0]?.addOn || []);
                            setVariantProductList(item?.variants);
                            setSelectedProductForCart({
                              barcodes: item?.variants?.[0]?.barcodes?.[0],
                              name: item?.name,
                              index: idx,
                              variantId: item?.variants?.[0]?.variantId,
                              sellingPrice,
                              variants: item?.variants,
                            });

                            setAddOnModalOpen(true);
                            return;
                          }

                          if (isWeighing) {
                            setOpenProductList(false);
                            setGoToMobileCategory(false);

                            increseItemQuantityTroughParticularCategory(
                              item?.variants?.[0]?.barcodes?.[0],
                              item?.name,
                              idx,
                              "weighingScale",
                              item?.variants?.[0]?.weightUnit,
                              item?.variants?.[0]?.variantId,
                              sellingPrice
                            );
                            return;
                          }

                          setAddItemLoader(idx);

                          increseItemQuantityTroughParticularCategory(
                            item?.variants?.[0]?.barcodes?.[0],
                            item?.name,
                            idx,
                            "Standard",
                            item?.variants?.[0]?.weightUnit,
                            item?.variants?.[0]?.variantId,
                            sellingPrice
                          );
                        }}
                        loading={addItemLoader === idx}
                      />
                    </div>
                  </div>
                );
              })}
            {productsLoader && (
              <div className="mobileProductLoader">
                <CircularProgress size="24px" />
              </div>
            )}

            {!productsLoader && productList.length === 0 && (
              <p>No Products Found for this Category</p>
            )}
          </div>
        </Box>
      </Drawer>

      <CartItemDrawer
        cartItemModalOpen={drawerOpen}
        setCartItemModalOpen={setDrawerOpen}
        cartItemData={drawerProduct}
      />
    </>
  );
};

export default MobileProductList;
